<div id="share-menu" class="p-4">
  @if (!data.isEvent) {
    <div class="flex items-center gap-3 mb-4">
      <span class="text-md font-semibold text-(--skz-text-primary)">{{ 'ShareMenu.Privacy' | translate }}</span>
      <ui-discreet-select
        [value]="privateValue"
        [options]="privacyOptions"
        (valueChange)="privateValue = $event; setPrivateValue()"
        [matTooltip]="getPrivacyTooltip() | translate"
        matTooltipShowDelay="1000"
        [disabled]="loading || isPrivatizeOptionLocked()"
      />
    </div>

    @if (privateValue === 'web') {
      <div class="mb-4 p-3 border border-(--skz-border-color-main) rounded-(--skz-radius-main) bg-(--skz-background-form-field-grey)">
        <!-- Public Link -->
        <div class="flex flex-col gap-1.5 mb-3">
          <mat-label class="form-label">{{ 'ShareMenu.PublicUrl' | translate }}</mat-label>
          <div class="flex items-center gap-1">
            <div class="flex-1 min-w-0 px-3 py-2.5 bg-white rounded-(--skz-radius-low) border border-(--skz-border-color-main) text-sm text-(--skz-text-muted) truncate select-all cursor-text">
              {{ shareUrl }}
            </div>
            <button mat-icon-button (click)="copyLink()" [matTooltip]="(linkCopied ? 'ShareMenu.LinkCopied' : 'ShareMenu.CopyLink') | translate" class="shrink-0 text-(--skz-primary-color)">
              <mat-icon>{{ linkCopied ? 'check' : 'content_copy' }}</mat-icon>
            </button>
            <button mat-icon-button (click)="regenerateLink($event)" [matTooltip]="'ShareMenu.RegenerateLink' | translate" class="shrink-0 text-(--skz-text-muted)">
              <mat-icon>rotate_left</mat-icon>
            </button>
          </div>
          <!-- Warning -->
          <p class="flex items-start gap-1.5 text-xs text-amber-700 leading-relaxed m-0 mt-1">
            <mat-icon class="text-amber-500 text-[14px]! h-3.5! w-3.5! shrink-0 mt-px">warning</mat-icon>
            {{ 'ShareMenu.PublicWarning' | translate }}
          </p>
        </div>

        <!-- Expiration Date -->
        <div class="flex flex-col gap-1.5 mb-3">
          <mat-label class="form-label">{{ 'ShareMenu.Expiration' | translate }}</mat-label>
          <div class="flex items-center gap-2">
            <mat-form-field subscriptSizing="dynamic" appearance="fill" class="flex-1 soft-input">
              <input matInput [matDatepicker]="picker" [ngModel]="shareExpirationDate" (ngModelChange)="onExpirationDateChange($event)" [placeholder]="'ShareMenu.Expiration' | translate" />
              <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
            </mat-form-field>
            @if (shareExpirationDate) {
              <button mat-icon-button (click)="onExpirationDateChange(null)" [matTooltip]="'Global.Clear' | translate" class="shrink-0 text-(--skz-text-muted)">
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>
        </div>

        <!-- Advanced Branding Section (collapsible) -->
        <div class="flex items-center justify-between pt-3 border-t border-(--skz-border-color-main) mt-3 cursor-pointer" (click)="showAdvancedSection = !showAdvancedSection">
          <div class="flex flex-col">
            <span class="text-sm font-medium text-(--skz-text-primary)">{{ 'ShareMenu.AdvancedSection' | translate }}</span>
            <span class="text-xs text-(--skz-text-muted)">{{ 'ShareMenu.AdvancedSectionHint' | translate }}</span>
          </div>
          <mat-icon class="text-(--skz-text-muted) transition-transform" [class.rotate-180]="showAdvancedSection">expand_more</mat-icon>
        </div>

        @if (showAdvancedSection) {
          <div class="flex flex-col gap-3 mt-3">
            <!-- Primary Color -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.EmbedColor' | translate }}</mat-label>
              <div class="flex items-center gap-2">
                <mat-form-field subscriptSizing="dynamic" appearance="fill" class="flex-1 soft-input">
                  <input matInput [(ngModel)]="sharePrimaryColor" [placeholder]="'ShareMenu.EmbedColorPlaceholder' | translate" (blur)="onColorInputBlur()" />
                </mat-form-field>
                <div
                  class="embed-color-swatch shrink-0"
                  [style.background-color]="isColorValid() && sharePrimaryColor ? sharePrimaryColor : '#5a73c5'"
                  (click)="openColorPicker($event)"
                  [matTooltip]="'ShareMenu.EmbedColorPick' | translate"
                ></div>
              </div>
              @if (!isColorValid()) {
                <span class="text-red-500 text-xs mt-0.5">{{ 'ShareMenu.InvalidColorError' | translate }}</span>
              }
            </div>

            <!-- Logo URL -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.EmbedLogo' | translate }}</mat-label>
              <mat-form-field subscriptSizing="dynamic" appearance="fill" class="w-full soft-input">
                <input matInput [(ngModel)]="shareLogoUrl" [placeholder]="'ShareMenu.EmbedLogoPlaceholder' | translate" (blur)="saveBranding()" />
              </mat-form-field>
            </div>

            <!-- Return URL -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.EmbedReturnUrl' | translate }}</mat-label>
              <mat-form-field subscriptSizing="dynamic" appearance="fill" class="w-full soft-input">
                <input matInput [(ngModel)]="shareReturnUrl" [placeholder]="'ShareMenu.EmbedReturnUrlPlaceholder' | translate" (blur)="saveBranding()" />
              </mat-form-field>
              <span class="text-[11px] text-(--skz-text-muted) leading-snug">
                {{ 'ShareMenu.EmbedReturnUrlHint' | translate }}
              </span>
            </div>

            <!-- Restricted Access -->
            <div class="flex flex-col gap-1.5 pt-3 border-t border-(--skz-border-color-main)">
              <mat-label class="form-label">{{ 'ShareMenu.ApprovedViewers' | translate }}</mat-label>
              <mat-form-field subscriptSizing="dynamic" appearance="fill" class="w-full soft-input">
                <mat-chip-grid #chipGrid aria-label="Approved viewers">
                  @for (email of shareApprovedViewers; track email) {
                    <mat-chip-row (removed)="removeApprovedViewer(email)">
                      {{ email }}
                      <button matChipRemove><mat-icon>cancel</mat-icon></button>
                    </mat-chip-row>
                  }
                  <input
                    [matChipInputFor]="chipGrid"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                    [matChipInputAddOnBlur]="true"
                    (matChipInputTokenEnd)="addApprovedViewer($event)"
                    (blur)="addViewerOnBlur($event)"
                    [placeholder]="'ShareMenu.ApprovedViewersPlaceholder' | translate"
                  />
                </mat-chip-grid>
              </mat-form-field>
              <span class="text-[11px] text-(--skz-text-muted) leading-snug">
                {{ 'ShareMenu.ApprovedViewersHint' | translate }}
              </span>
            </div>

            <!-- Access Password -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.SharePassword' | translate }}</mat-label>
              <mat-form-field subscriptSizing="dynamic" appearance="fill" class="w-full soft-input">
                <input matInput [(ngModel)]="sharePassword" [placeholder]="'ShareMenu.SharePasswordPlaceholder' | translate" (blur)="saveBranding()" />
              </mat-form-field>
            </div>

            <!-- Allow Download -->
            <div
              class="flex items-center justify-between pt-3 border-t border-(--skz-border-color-main)"
              [matTooltip]="!canAllowDownload ? ('ShareMenu.AllowDownloadDisabledTooltip' | translate) : ''"
              [matTooltipPosition]="'above'"
            >
              <div class="flex flex-col" [class.opacity-50]="!canAllowDownload">
                <span class="text-sm font-medium text-(--skz-text-primary)">{{ 'ShareMenu.AllowDownload' | translate }}</span>
                <span class="text-xs text-(--skz-text-muted)">{{ 'ShareMenu.AllowDownloadHint' | translate }}</span>
              </div>
              <mat-slide-toggle [checked]="shareAllowDownload" [disabled]="!canAllowDownload" (change)="toggleAllowDownload($event.checked)" color="primary"></mat-slide-toggle>
            </div>

            <!-- Send Invites Button -->
            <div class="flex flex-col gap-2 pt-3 border-t border-(--skz-border-color-main)">
              <!-- Info hint row -->
              <div class="flex items-start gap-1.5">
                <mat-icon class="text-[14px]! h-3.5! w-3.5! shrink-0 mt-px" [class.text-amber-500]="!canAllowDownload" [class.text-(--skz-text-muted)]="canAllowDownload">info</mat-icon>
                <span class="text-[11px] leading-snug" [class.text-amber-700]="!canAllowDownload" [class.text-(--skz-text-muted)]="canAllowDownload">
                  {{ 'ShareMenu.SendInvitesHint' | translate }}
                </span>
              </div>

              <button
                mat-flat-button
                color="primary"
                class="w-full"
                [disabled]="sendingInvites || !sharePassword || sharePassword === PASSWORD_PLACEHOLDER || shareApprovedViewers.length === 0"
                [matTooltip]="
                  !sharePassword || shareApprovedViewers.length === 0
                    ? ('ShareMenu.SendInvitesDisabledTooltip' | translate)
                    : sharePassword === PASSWORD_PLACEHOLDER
                      ? ('ShareMenu.SendInvitesHashHint' | translate)
                      : ''
                "
                (click)="sendShareInvites()"
              >
                @if (sendingInvites) {
                  <mat-progress-spinner mode="indeterminate" [diameter]="18"></mat-progress-spinner>
                } @else {
                  <span>
                    {{ (sendingInvites ? 'ShareMenu.SendInvitesSending' : 'ShareMenu.SendInvites') | translate }}
                  </span>
                }
              </button>
            </div>
          </div>
        }

        <!-- Embed Section -->
        <div class="flex items-center justify-between pt-3 border-t border-(--skz-border-color-main) mt-3">
          <div class="flex flex-col">
            <span class="text-sm font-medium text-(--skz-text-primary)">{{ 'ShareMenu.EmbedTitle' | translate }}</span>
            <span class="text-xs text-(--skz-text-muted)">{{ 'ShareMenu.EmbedTitleHint' | translate }}</span>
          </div>
          <mat-slide-toggle [checked]="showEmbedSection" (change)="showEmbedSection = $event.checked" color="primary"></mat-slide-toggle>
        </div>

        @if (showEmbedSection) {
          <div class="flex flex-col gap-3 mt-3">
            <!-- Mode Selector -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.EmbedMode' | translate }}</mat-label>
              <div class="flex gap-2">
                <div class="embed-mode-option flex-1" [class.embed-mode-active]="embedMode === 'full'" (click)="embedMode = 'full'">
                  <mat-icon class="text-[16px]! h-4! w-4!">fullscreen</mat-icon>
                  <span>{{ 'ShareMenu.EmbedModeFull' | translate }}</span>
                </div>
                <div class="embed-mode-option flex-1" [class.embed-mode-active]="embedMode === 'partial'" (click)="embedMode = 'partial'">
                  <mat-icon class="text-[16px]! h-4! w-4!">crop_free</mat-icon>
                  <span>{{ 'ShareMenu.EmbedModePartial' | translate }}</span>
                </div>
              </div>
              <span class="text-[11px] text-(--skz-text-muted) leading-snug">
                {{ (embedMode === 'full' ? 'ShareMenu.EmbedModeFullHint' : 'ShareMenu.EmbedModePartialHint') | translate }}
              </span>
            </div>

            <!-- Language -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.EmbedLang' | translate }}</mat-label>
              <div class="flex gap-2">
                <div class="embed-mode-option flex-1" [class.embed-mode-active]="embedLang === ''" (click)="embedLang = ''">
                  <span>{{ 'ShareMenu.EmbedLangAuto' | translate }}</span>
                </div>
                <div class="embed-mode-option flex-1" [class.embed-mode-active]="embedLang === 'fr'" (click)="embedLang = 'fr'">
                  <span>Français</span>
                </div>
                <div class="embed-mode-option flex-1" [class.embed-mode-active]="embedLang === 'en'" (click)="embedLang = 'en'">
                  <span>English</span>
                </div>
              </div>
            </div>

            <!-- Generated Code Snippet -->
            <div class="flex flex-col gap-1.5">
              <mat-label class="form-label">{{ 'ShareMenu.EmbedCode' | translate }}</mat-label>
              <div class="flex items-start gap-1">
                <div class="embed-code-block flex-1 min-w-0">
                  <code class="text-[11px] leading-relaxed break-all select-all">{{ iframeCode }}</code>
                </div>
                <button
                  mat-icon-button
                  (click)="copyEmbedCode()"
                  [matTooltip]="(embedCodeCopied ? 'ShareMenu.EmbedCodeCopied' : 'ShareMenu.EmbedCodeCopy') | translate"
                  class="shrink-0 text-(--skz-primary-color)"
                >
                  <mat-icon>{{ embedCodeCopied ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
              <p class="flex items-start gap-1.5 text-[11px] text-(--skz-text-muted) leading-snug m-0">
                <mat-icon class="text-[14px]! h-3.5! w-3.5! shrink-0 mt-px text-(--skz-text-muted)">info</mat-icon>
                {{ 'ShareMenu.EmbedCodeHint' | translate }}
              </p>
            </div>
          </div>
        }
      </div>
    }
  }

  @if (canEdit || !data.isEvent) {
    <div id="add-members" class="mb-4">
      <span class="text-sm font-semibold text-(--skz-text-primary)">{{ 'ShareMenu.AddMembers' | translate }}</span>
      <div class="mt-2.5">
        <app-user-group-search
          [placeholder]="'ShareMenu.SearchPlaceholder' | translate"
          iconPosition="prefix"
          mode="with-button"
          (buttonClick)="addEntity($event)"
          [buttonText]="'ShareMenu.Add' | translate"
        />
      </div>
    </div>
  }
  <div id="current-members">
    <span class="text-sm font-semibold text-(--skz-text-primary)">{{ 'ShareMenu.CurrentMembers' | translate }}</span>
    <mat-form-field class="w-full my-2" appearance="fill">
      <input type="text" matInput [(ngModel)]="filterTerm" (keyup)="filterUsers()" autocomplete="off" [placeholder]="'ShareMenu.FilterMembers' | translate" />
      <mat-icon matPrefix>filter_list</mat-icon>
    </mat-form-field>
    <div id="members-list" class="custom-scrollbar">
      @for (member of displayedUsers; track $index) {
        <div class="flex items-center justify-between mb-2">
          <ui-user [name]="member.name?.trim()" [imageUrl]="member.photo" [description]="getEmailOrType(member)" />
          <div class="flex items-center">
            <app-role-selector [value]="member.userRole" (valueChange)="setUserRole(member, $event)" />
            @if (canEdit || !data.isEvent) {
              <button
                mat-icon-button
                color="warn"
                [disabled]="!this.pathID && !this.questionID && !this.templateID && (!member.userRole || calendarService.newEventUsersSource.value.length == 1)"
                (click)="deleteUser(member)"
              >
                <mat-icon>delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>
  </div>
</div>
